import React, { useState, useEffect } from 'react';
import { Share2, X, Copy, Check, Download } from 'lucide-react';
import QRCode from 'qrcode';

interface Props {
  registryId: string;
  onClose: () => void;
}

export default function ShareRegistryModal({ registryId, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Use absolute URL for sharing
  const shareUrl = `${window.location.protocol}//${window.location.host}/rsvp/${registryId}`;

  useEffect(() => {
    generateQRCode();
  }, [shareUrl]);

  const generateQRCode = async () => {
    try {
      const url = await QRCode.toDataURL(shareUrl, {
        width: 300,
        margin: 2, /* This is for the QR code color */
        color: {
          dark: '#FFFFFF',
          light: '#000000'
        }
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error('Error generating QR code:', err);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = 'registry-qr-code.png';
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-md w-full relative">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Share2 className="w-5 h-5 text-white" />
            <h3 className="text-lg font-semibold text-white">Share Link</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-black p-4 rounded-lg">
              {qrCodeUrl && <img src={qrCodeUrl} alt="Registry QR Code" className="w-48 h-48" />}
            </div>
            <button
              onClick={downloadQRCode}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
            >
              <Download className="w-4 h-4" />
              <span>Download QR Code</span>
            </button>
            <p className="text-sm text-gray-300 text-center">
              Scan this QR code to RSVP or download it to include in invitations
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Share Link
            </label>
            <div className="flex">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 bg-gray-900/50 border border-r-0 border-gray-700 rounded-l-md text-white focus:ring-primary focus:border-primary"
              />
              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-primary text-white rounded-r-md hover:bg-primary-600 flex items-center"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}